import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { ASSISTANT_ROUTE } from '../../lib/assistantBus.js';
import { kindOfFile } from '../../lib/assistantAttachments.js';
import { closeAllLayers } from '../../lib/layers.js';
import { characterImage, DEFAULT_CHARACTER } from '../../lib/characters.js';
import { listConversations } from '../../lib/matrix/rooms.js';
import { useMatrixClient } from '../../lib/matrix/useMatrix.js';
import { setSecretaryShare, setShareDraft, setShareFiles } from '../../lib/shareDraft.js';
import { useProfile } from '../../lib/useProfile.js';
import { conversationAvatar } from '../../pages/conversaAvatar.js';
import { PersonAvatar } from '../chrome/PersonAvatar.js';
import { MxcPhoto } from '../chrome/PersonPhoto.js';
import '../../pages/ported/conversas-extra.css';
import './chat-extras.css';

/** Miniaturas do que foi compartilhado (fotos) e o nome dos demais arquivos. */
function SharedPreview({ files }: { files: File[] }) {
  const urls = useMemo(
    () =>
      files.map((file) =>
        /^image\/(jpeg|png|webp|gif)$/.test(file.type) ? URL.createObjectURL(file) : null,
      ),
    [files],
  );
  useEffect(() => () => urls.forEach((url) => url && URL.revokeObjectURL(url)), [urls]);
  return (
    <ul className="fw-files" aria-label="O que vai junto">
      {files.map((file, index) => (
        <li key={`${file.name}-${index}`} className={urls[index] ? 'pic' : 'doc'}>
          {urls[index] ? (
            <img src={urls[index]!} alt="" />
          ) : (
            <>
              <b>{(file.name.match(/\.([a-z0-9]{2,5})$/i)?.[1] ?? 'arq').toUpperCase()}</b>
              <span>{file.name}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * "Mandar para": escolhe quem recebe o que foi compartilhado. O Secretário vem primeiro e
 * recebe na hora (e já responde); numa conversa com uma pessoa, o texto e os arquivos ficam
 * prontos no campo, para conferir, escrever junto e enviar.
 */
export function ShareSheet({
  text,
  files = [],
  notice = null,
  onClose,
}: {
  text: string;
  files?: File[];
  notice?: string | null;
  onClose: () => void;
}) {
  const { client } = useMatrixClient();
  const [, navigate] = useLocation();
  const profileQ = useProfile();
  const [query, setQuery] = useState('');
  const conversations = useMemo(() => (client ? listConversations(client) : []), [client]);
  const wanted = query.trim().toLowerCase();
  const visible = wanted
    ? conversations.filter((item) => item.name.toLowerCase().includes(wanted))
    : conversations;
  const secretaryName = profileQ.data?.secretaryName?.trim() || 'Meu Secretário';
  const characterKey = profileQ.data?.characterKey ?? DEFAULT_CHARACTER;
  // O Secretário lê foto, PDF e arquivos de escritório; vídeo e formatos antigos, não.
  const readable = files.filter((file) => kindOfFile(file) !== null);
  const secretaryCanRead = files.length === 0 || readable.length > 0;

  function pick(roomId: string) {
    setShareDraft(roomId, text);
    setShareFiles(roomId, files);
    onClose();
    closeAllLayers();
    navigate(`/conversas/${encodeURIComponent(roomId)}`);
  }

  function toSecretary() {
    setSecretaryShare(text, readable);
    onClose();
    closeAllLayers();
    navigate(ASSISTANT_ROUTE);
  }

  return createPortal(
    <div className="cv-sheetwrap" onClick={onClose}>
      <div
        className="cv-sheet fw"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-label="Mandar para"
      >
        <div className="cv-grip" />
        <div className="cv-sheet-h">Mandar para</div>
        {files.length > 0 && <SharedPreview files={files} />}
        {text && <p className="fw-preview">{text}</p>}
        {notice && <p className="fw-notice">{notice}</p>}
        {secretaryCanRead && (
          <button type="button" className="fw-secretary" onClick={toSecretary}>
            <span className="fw-secretary-av">
              <img src={characterImage(characterKey, 'simpatico')} alt="" />
            </span>
            <span className="fw-name">
              <b>{secretaryName}</b>
              <small>
                {files.length
                  ? 'Ele lê e já te responde'
                  : 'Ele lê e já te responde, na conversa com ele'}
              </small>
            </span>
            <i aria-hidden="true">›</i>
          </button>
        )}
        <input
          className="cv-sheet-in fw-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar conversa"
          aria-label="Buscar conversa"
        />
        <ul className="fw-list">
          {!client && <li className="fw-empty">Abrindo suas conversas…</li>}
          {client && visible.length === 0 && (
            <li className="fw-empty">Nenhuma conversa com esse nome.</li>
          )}
          {client &&
            visible.map((item) => {
              const avatar = conversationAvatar(item);
              return (
                <li key={item.roomId}>
                  <button type="button" onClick={() => pick(item.roomId)}>
                    <PersonAvatar
                      className="fw-av"
                      name={item.name}
                      color={avatar.c}
                      group={avatar.group}
                    >
                      <MxcPhoto client={client} mxc={item.avatar} />
                    </PersonAvatar>
                    <span className="fw-name">
                      <b>{item.name}</b>
                      <small>
                        {item.isDirect ? 'Conversa' : `Grupo · ${item.memberCount} pessoas`}
                      </small>
                    </span>
                    <i aria-hidden="true">›</i>
                  </button>
                </li>
              );
            })}
        </ul>
        <button className="cv-cancel" type="button" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </div>,
    document.body,
  );
}
